/**
 * SoundConfigFlyover — Mode-specific flyover popover for the SoundExpert bottom toolbar.
 *
 * Renders an absolutely-positioned popover above the toolbar anchor element.
 * Content varies by audio mode:
 *   - SFX: Duration (Auto/1s/5s/Custom) + Loop (Off/On)
 *   - Music: Duration (Auto/30s/60s/Custom) + Lyrics (Auto/Custom/Instrumental)
 *   - Speech: Voice profile selector (searchable list)
 *
 * Uses nopan/nodrag/nowheel + stopPropagation for ReactFlow isolation.
 * Dismissed by click-outside or Escape key.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { VoiceProfileDict } from '../../../../generated/nodeDataTypes'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SoundConfigFlyoverProps {
  mode: string
  duration: string
  loop: string
  lyricsMode: string
  voiceProfile?: VoiceProfileDict
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
  onClose: () => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const SFX_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '1s', value: '1s' },
  { label: '5s', value: '5s' },
  { label: '10s', value: '10s' },
]

const MUSIC_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '30s', value: '30s' },
  { label: '60s', value: '60s' },
  { label: '120s', value: '120s' },
]

const LOOP_OPTIONS = [
  { label: 'Off', value: 'off' },
  { label: 'On', value: 'on' },
]

const LYRICS_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Custom', value: 'custom' },
  { label: 'Instrumental', value: 'instrumental' },
]

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
}

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'border text-foreground'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

function segmentedBtnStyle(isSelected: boolean) {
  return isSelected
    ? { background: ACCENT.bg20, borderColor: ACCENT.bg40, color: ACCENT.base }
    : undefined
}

// ─── Component ──────────────────────────────────────────────────────────────

export const SoundConfigFlyover = memo(function SoundConfigFlyover({
  mode,
  duration,
  loop,
  lyricsMode,
  voiceProfile,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onVoiceProfileChange,
  onClose,
}: SoundConfigFlyoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // ── Click-outside dismissal ─────────────────────────────────────────────
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    // Use setTimeout to avoid immediately closing from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [onClose])

  // ── Escape key dismissal ────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleDurationClick = useCallback(
    (value: string) => onDurationChange(value),
    [onDurationChange],
  )

  // ── Render SFX config ───────────────────────────────────────────────────
  const renderSfxConfig = () => (
    <div className="flex flex-col gap-3" data-testid="sfx-config">
      <div>
        <label className={LABEL_CLASS}>Duration</label>
        <div className="flex gap-1">
          {SFX_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              style={segmentedBtnStyle(duration === opt.value)}
              data-testid={`sfx-duration-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>Loop</label>
        <div className="flex gap-1">
          {LOOP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLoopChange(opt.value)}
              className={segmentedBtnClass(loop === opt.value)}
              style={segmentedBtnStyle(loop === opt.value)}
              data-testid={`loop-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // ── Render Music config ─────────────────────────────────────────────────
  const renderMusicConfig = () => (
    <div className="flex flex-col gap-3" data-testid="music-config">
      <div>
        <label className={LABEL_CLASS}>Music Duration</label>
        <div className="flex gap-1">
          {MUSIC_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              style={segmentedBtnStyle(duration === opt.value)}
              data-testid={`music-duration-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>Lyrics</label>
        <div className="flex gap-1">
          {LYRICS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLyricsModeChange(opt.value)}
              className={segmentedBtnClass(lyricsMode === opt.value)}
              style={segmentedBtnStyle(lyricsMode === opt.value)}
              data-testid={`lyrics-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // ── Render Speech config (voice selector) ───────────────────────────────
  const renderSpeechConfig = () => (
    <div className="flex flex-col gap-2" data-testid="speech-config">
      <label className={LABEL_CLASS}>Voice Profile</label>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search voices..."
        className="w-full text-xs bg-surface-1 text-text-primary border border-border-subtle rounded px-2 py-1.5 focus:outline-none focus:border-cyan-500/50 transition-colors placeholder:text-text-muted/40"
        data-testid="voice-search-input"
      />
      {voiceProfile ? (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-surface-1 border border-border-subtle">
          <span className="text-xs">{'\u{1F399}\uFE0F'}</span>
          <span className="text-xs text-foreground truncate">{voiceProfile.voice_name}</span>
          <button
            onClick={() => onVoiceProfileChange?.(undefined)}
            className="ml-auto text-xxs text-text-muted hover:text-foreground"
            data-testid="clear-voice-btn"
          >
            Clear
          </button>
        </div>
      ) : (
        <div className="text-xs text-text-muted/60 px-2 py-3 text-center">
          No voice selected. Connect a character node to use speech mode.
        </div>
      )}
    </div>
  )

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 right-0 mb-2 z-50 nopan nodrag nowheel"
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="sound-config-flyover"
    >
      <div className="bg-surface-0 border border-border rounded-lg shadow-xl p-3 mx-1">
        {mode === 'sfx' && renderSfxConfig()}
        {mode === 'music' && renderMusicConfig()}
        {mode === 'speech' && renderSpeechConfig()}
      </div>
    </div>
  )
})

SoundConfigFlyover.displayName = 'SoundConfigFlyover'
